import { X } from 'lucide-react';
import { ButtonHTMLAttributes, HTMLAttributes, MouseEvent, ReactNode } from 'react';

import { cn } from './cn';
import { StatusBadgeTone } from './StatusBadge';

const toneClassNames: Record<StatusBadgeTone, { solid: string; soft: string; outline: string }> = {
  neutral: {
    solid: 'border-ink-muted bg-ink-muted text-ink-inverse',
    soft: 'border-line bg-canvas text-ink',
    outline: 'border-line-strong bg-transparent text-ink',
  },
  brand: {
    solid: 'border-brand bg-brand text-ink-inverse',
    soft: 'border-brand/20 bg-brand-subtle text-brand',
    outline: 'border-brand bg-transparent text-brand',
  },
  positive: {
    solid: 'border-positive bg-positive text-ink-inverse',
    soft: 'border-positive/20 bg-positive-subtle text-positive',
    outline: 'border-positive bg-transparent text-positive',
  },
  caution: {
    solid: 'border-caution bg-caution text-on-caution',
    soft: 'border-caution/20 bg-caution-subtle text-caution-ink',
    outline: 'border-caution bg-transparent text-caution-ink',
  },
  critical: {
    solid: 'border-critical bg-critical text-ink-inverse',
    soft: 'border-critical/20 bg-critical-subtle text-critical',
    outline: 'border-critical bg-transparent text-critical',
  },
  informative: {
    solid: 'border-informative bg-informative text-ink-inverse',
    soft: 'border-informative/20 bg-informative-subtle text-informative',
    outline: 'border-informative bg-transparent text-informative',
  },
  gold: {
    solid: 'border-gold bg-gold text-on-caution',
    soft: 'border-gold/30 bg-gold-subtle text-gold',
    outline: 'border-gold bg-transparent text-gold',
  },
  silver: {
    solid: 'border-silver bg-silver text-ink-inverse',
    soft: 'border-silver/30 bg-silver-subtle text-ink',
    outline: 'border-silver bg-transparent text-silver',
  },
  bronze: {
    solid: 'border-bronze bg-bronze text-ink-inverse',
    soft: 'border-bronze/30 bg-bronze-subtle text-bronze',
    outline: 'border-bronze bg-transparent text-bronze',
  },
};

export interface ChipProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'onClick'> {
  tone?: StatusBadgeTone;
  variant?: 'solid' | 'soft' | 'outline';
  size?: 'small' | 'medium';
  icon?: ReactNode;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  onDelete?: (event: MouseEvent<HTMLButtonElement>) => void;
  deleteLabel?: string;
}

export const Chip = ({
  children,
  className,
  deleteLabel = 'Remove',
  icon,
  onClick,
  onDelete,
  size = 'medium',
  tone = 'neutral',
  variant = 'soft',
  ...props
}: ChipProps) => {
  const chipClassName = cn(
    'inline-flex max-w-full items-center gap-1 rounded-full border font-medium leading-none',
    size === 'small' ? 'h-5 px-2 text-xs' : 'h-6 px-2.5 text-xs',
    toneClassNames[tone][variant],
    onClick && 'cursor-pointer transition-opacity hover:opacity-80',
    className,
  );
  const label = (
    <>
      {icon && <span className="inline-flex shrink-0">{icon}</span>}
      <span className="truncate">{children}</span>
    </>
  );
  const deleteButton = onDelete && (
    <button
      type="button"
      aria-label={deleteLabel}
      className="-mr-1 inline-flex size-4 shrink-0 items-center justify-center rounded-full opacity-70 hover:opacity-100"
      onClick={event => {
        event.stopPropagation();
        onDelete(event);
      }}
    >
      <X className="size-3" />
    </button>
  );

  if (onClick && !onDelete) {
    return (
      <button
        type="button"
        className={chipClassName}
        onClick={onClick}
        {...(props as ButtonHTMLAttributes<HTMLButtonElement>)}
      >
        {label}
      </button>
    );
  }
  return (
    <span className={chipClassName} {...props}>
      {onClick ? (
        <button type="button" className="inline-flex min-w-0 items-center gap-1" onClick={onClick}>
          {label}
        </button>
      ) : (
        label
      )}
      {deleteButton}
    </span>
  );
};
